import developmentAccessors from "../../../../state/development/utils/developmentAccessors";
import { Development } from  "../../../../types/Development/Development";
import { VariableId } from  "../../../../types/VariableId";
import generateLinearRegressionPreset from "./generatorForLinearRegression";

/**
 * Generate the multifamily buildables presets. Only one bedroom units will be initialized to non zero values.
 */
const generatePresets = (development: Development, targetArea: number): object => {
  const lossFactor = developmentAccessors.getInputValue(development, VariableId.MultifamilyLossFactor) as number;
  const unitArea = developmentAccessors.getInputValue(development, VariableId.MultifamilyTwoBedArea) as number;

  const microUnitBedroomArea = developmentAccessors.getInputValue(development, VariableId.MultifamilyMicrounitArea) as number;
  const studioBedroomArea = developmentAccessors.getInputValue(development, VariableId.MultifamilyStudioArea) as number;
  const oneBedroomArea = developmentAccessors.getInputValue(development, VariableId.MultifamilyOneBedArea) as number;
  const twoBedroomArea = developmentAccessors.getInputValue(development, VariableId.MultifamilyTwoBedArea) as number;
  const threeBedroomArea = developmentAccessors.getInputValue(development, VariableId.MultifamilyThreeBedArea) as number;

  const usableArea = (targetArea * (1 - lossFactor));
  let amenitiesArea = usableArea * 0.1;

  // Guard against amenities consuming all of the usable area.
  if (usableArea - amenitiesArea < unitArea) amenitiesArea = 0;

  const areaAvailableForUnits = usableArea ? usableArea - amenitiesArea : 0;
  const unitQuantity = unitArea ? Math.floor(areaAvailableForUnits / unitArea) : 0;
  const leftoverArea = areaAvailableForUnits - (unitQuantity * unitArea);
  amenitiesArea += leftoverArea;

  const previousMaximumUnits = developmentAccessors.getInputMaximum(development,  VariableId.MultifamilyOneBedQuantityToggleable);
  const previousMaximumAmenities = developmentAccessors.getInputMaximum(development,  VariableId.MultifamilyAmenitiesAreaToggleable);

  return {
    [VariableId.MultifamilyOneBedQuantityToggleable]: {
      "minimum":  0,
      "value":    0,
      "maximum":  2 * unitQuantity || previousMaximumUnits
    },
    [VariableId.MultifamilyMicrounitQuantityToggleable]: {
      "minimum":  0,
      "value":    0,
      "maximum":  2 * unitQuantity || previousMaximumUnits
    },
    [VariableId.MultifamilyStudioQuantityToggleable]: {
      "minimum":  0,
      "value":    0,
      "maximum":  2 * unitQuantity || previousMaximumUnits
    },
    [VariableId.MultifamilyTwoBedQuantityToggleable]: {
      "minimum":  0,
      "value":    unitQuantity,
      "maximum":  2 * unitQuantity || previousMaximumUnits
    },
    [VariableId.MultifamilyThreeBedQuantityToggleable]: {
      "minimum":  0,
      "value":    0,
      "maximum":  2 * unitQuantity || previousMaximumUnits
    },
    [VariableId.MultifamilyAmenitiesAreaToggleable]: {
      "minimum": 0,
      "value": amenitiesArea,
      "maximum": 10 * amenitiesArea || previousMaximumAmenities
    },
    [VariableId.MultifamilyYearOneMonthlyRentPerMicrounit]: generateLinearRegressionPreset(development, VariableId.MultifamilyYearOneMonthlyRentPerMicrounit, microUnitBedroomArea),
    [VariableId.MultifamilyYearOneMonthlyRentPerStudio]: generateLinearRegressionPreset(development, VariableId.MultifamilyYearOneMonthlyRentPerStudio, studioBedroomArea),
    [VariableId.MultifamilyYearOneMonthlyRentPerOneBed]: generateLinearRegressionPreset(development, VariableId.MultifamilyYearOneMonthlyRentPerOneBed, oneBedroomArea),
    [VariableId.MultifamilyYearOneMonthlyRentPerTwoBed]: generateLinearRegressionPreset(development, VariableId.MultifamilyYearOneMonthlyRentPerTwoBed, twoBedroomArea),
    [VariableId.MultifamilyYearOneMonthlyRentPerThreeBed]: generateLinearRegressionPreset(development, VariableId.MultifamilyYearOneMonthlyRentPerThreeBed, threeBedroomArea),
    [VariableId.MultifamilyHardCostPerArea]: generateLinearRegressionPreset(development, VariableId.MultifamilyHardCostPerArea),
  };
}

export default generatePresets;
